import * as THREE from 'three';

export default class GradientPlane {
    scene;
    camera;
    texture;
    plane;
    material;
    geometry;
    textureloader;
    element;
    triggers = [];

    ctx;

    options = {};

    defaultOptions = {
        speed: 1,       // Speed on hover
        baseSpeed: 0,   // Default speed
        color: [58, 70, 148],
    };

    coeff = 1;
    scrollCoeff = 1;

    stop = false;

    vertexShader = `
        varying vec2 vUv;
        varying vec3 pos;
        uniform float time;
        void main() {
            vUv = uv;
            // uncomment for little bit scale texture
            vUv = (vUv - 0.5) * 0.85 + 0.5;
            vec4 normalizedPos = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
            gl_Position = normalizedPos;
        }
    `;

    fragmentShader = `
        uniform sampler2D texture1; 
        uniform float time;
        uniform vec3 baseColor;
        varying vec2 vUv;
        varying vec3 pos;
      
        // Water random from shadertoy.com
        vec2 GetGradient(vec2 intPos, float t) {
            // Uncomment for calculated rand
            float rand = fract(sin(dot(intPos, vec2(12.9898, 78.233))) * 43758.5453);;
            // Texture-based rand (a bit faster on my GPU)
            //float rand = texture(iChannel0, intPos / 64.0).r;
            // Rotate gradient: random starting rotation, random rotation rate
            float angle = 6.283185 * rand + 4.0 * t * rand;
            return vec2(cos(angle), sin(angle));
        }
        
        float Pseudo3dNoise(vec3 pos) {
            vec2 i = floor(pos.xy);
            vec2 f = pos.xy - i;
            vec2 blend = f * f * (3.0 - 2.0 * f);
            float noiseVal = 
                mix(
                    mix(
                        dot(GetGradient(i + vec2(0, 0), pos.z), f - vec2(0, 0)),
                        dot(GetGradient(i + vec2(1, 0), pos.z), f - vec2(1, 0)),
                        blend.x),
                    mix(
                        dot(GetGradient(i + vec2(0, 1), pos.z), f - vec2(0, 1)),
                        dot(GetGradient(i + vec2(1, 1), pos.z), f - vec2(1, 1)),
                        blend.x),
                blend.y
            );
            return noiseVal / 0.2; // normalize to about [-1..1]
        }

        float grid(vec2 st, float res) {
            vec2 grid = fract(st*res);
            return (step(res,grid.x) * step(res,grid.y));
        }

        void main() {
            float shift = 0.5;
            vec2 copyvuv = vUv;

            float noiseVal = Pseudo3dNoise(vec3(vUv * 3.0, time * 0.05));

            copyvuv.x += noiseVal * 0.1;
            copyvuv.y += noiseVal * 0.1;

            vec4 color = vec4(baseColor, 1.0);
            vec2 grid_uv = copyvuv.xy * 50.0; // scale
            float x = grid(vec2(1.0, grid_uv.y), 0.6); // resolution
            gl_FragColor = color * x;
            //gl_FragColor.a = 1.0;
        }
    `;

    get width() {
        return this.getWidth();
    }

    get height() {
        return this.getHeight();
    }

    constructor(element, options = this.options) {
        this.element = element;

        if (!this.element) {
            return;
        }
        this.element.gradientInstance = this;
        
        this.options = Object.assign(this.defaultOptions, options);
        this.speed = this.options.speed;
        this.baseSpeed = this.options.baseSpeed;
        this.color = this.options.color.map(c => c/255);
    }

    init() {
        if (this.inited) {
            return;
        }
        this.element.classList.remove('hide');
        this.inited = true;
        this.initScene();
        this.createMesh();
        this.findTrigger();
        this.addListeners();
    }

    initScene() {
        this.scene = new THREE.Scene();
        this.camera = new THREE.OrthographicCamera(
            this.width * -0.5,
            this.width * 0.5,
            this.height * 0.5,
            this.height * -0.5,
            -1,
            1
        );

        this.ctx = document.createElement('canvas').getContext('2d');
        this.element.appendChild(this.ctx.canvas);
    }

    createMesh() {
        const size = this.width >= this.height ? this.width : this.height;
        this.geometry = new THREE.PlaneGeometry(1, 1);
        this.material = new THREE.ShaderMaterial({
            side: THREE.DoubleSide,
            uniforms: {
                time: {value: 1.0},
                resolution: {value: new THREE.Vector2()},
                baseColor: {value: new THREE.Vector3(...this.color)}
            },
            vertexShader: this.vertexShader,
            fragmentShader: this.fragmentShader,
        }),
        
        this.plane = new THREE.Mesh(this.geometry, this.material);
        this.plane.scale.set(size, size, 1);

        if (this.scene) {
            this.scene.add(this.plane);
        }
    }

    render(renderer) {

        if (this.stop) {
            return;
        }
        
        this.speed += (this.baseSpeed - this.speed) * 0.07;
        this.speed = +this.speed.toFixed(5);

        this.coeff += (this.scrollCoeff - this.coeff) * 0.03;

        if (this.material) {
            this.material.uniforms.time.value += (0.05 * this.speed) * this.coeff;
        }
        
        if (renderer) {
            renderer.render(this.scene, this.camera);
        }
    }

    addListeners() {
        if (this.listenersAdded) {
            return;
        }

        this.listenersAdded = true;
        window.addEventListener('resize', () => {
            this.onResize();
        });

        const hoverEls = this.triggers ?? [this.element];

        hoverEls.forEach(hoverEl => {
            hoverEl.addEventListener('mousemove', () => {
                this.baseSpeed = this.options.speed;
            });
            hoverEl.addEventListener('mouseleave', () => {
                this.baseSpeed = this.options.baseSpeed;
            });
        });
    }

    onResize() {                                                           
        if (this.plane) {
            const size = this.width >= this.height ? this.width : this.height;
            this.plane.scale.set(size, size, 1);
        }

        if (this.camera) {
            this.camera.left = this.width * -0.5;
            this.camera.right = this.width * 0.5;
            this.camera.top = this.height * 0.5;
            this.camera.bottom = this.height * -0.5;
            this.camera.updateProjectionMatrix();
        }
    }

    getWidth() {
        return this.element.clientWidth * 4;
    }

    getHeight() {
        return this.element.clientHeight * 4;
    }

    findTrigger() {
        const triggerSelector = this.element.dataset.triggerSelector ?? false;
        const triggerRel = this.element.dataset.triggerRel ?? false;

        if (!triggerRel || !triggerSelector) {
            return;
        }

        if (triggerRel === 'sibling') {
            this.triggers = Array.from(this.element.parentNode.querySelectorAll(triggerSelector));
        }

        if (triggerRel === 'parent') {
            this.triggers = [this.element.closest(triggerSelector)];
        }
    }

    interpolation(value, min, max, newMin, newMax) {
		let newValue = ( (value-min) / (max-min) ) * (newMax-newMin) + newMin;
		return newValue;
    }
};
